// @ts-nocheck
import axios from 'axios';
import { User, tokenManager } from '../store/authStore';

// Configure axios defaults
// BUGFIX: Use internal API routes instead of external domains
// EPIC86 FIX: Direct property access for esbuild replacement (no conditional check)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Log the API URL being used (for debugging)

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // BUGFIX: Increased from 8s to 15s to match profile endpoint needs (multiple DB queries)
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  // Firefox and Safari compatibility settings
  withCredentials: true, // Enable credentials for CORS with auth backend
  // Remove custom validateStatus to use default behavior for better mobile compatibility
});

// Request interceptor to add auth token and test mode headers
api.interceptors.request.use(config => {
  // CRITICAL BUGFIX: Simplify token handling to use raw JWT tokens
  // Previous encryption/decryption was causing authentication failures

  let token = null;

  // First: Check for JWT token in localStorage (stored by login/register)
  token = localStorage.getItem('sigma08_jwt_token');

  if (!token) {
    // Second: Check sessionStorage as backup
    token = sessionStorage.getItem('sigma08_jwt_token');
  }

  if (!token) {
    // Third: Check for access_token (standard key)
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      // Check if this is already a JWT (has 3 parts separated by dots)
      if (accessToken.includes('.') && accessToken.split('.').length === 3) {
        token = accessToken;
      }
    }
  }

  if (!token) {
    // Fourth: Check sessionStorage for access_token
    const sessionAccessToken = sessionStorage.getItem('access_token');
    if (sessionAccessToken) {
      // Check if this is already a JWT (has 3 parts separated by dots)
      if (sessionAccessToken.includes('.') && sessionAccessToken.split('.').length === 3) {
        token = sessionAccessToken;
      }
    }
  }

  // Fifth: Check for auth_token cookie
  if (!token) {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'auth_token' && value) {
        token = decodeURIComponent(value);
        break;
      }
    }
  }

  // NOTE: Removed test token generation as it was causing authentication failures
  // The backend now provides real JWT tokens that should be used consistently

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add demo mode header for staging environment
  const isStaging = window.location.hostname.includes('staging') || window.location.hostname === 'localhost';
  if (isStaging) {
    config.headers['x-demo-mode'] = 'true';
  }

  return config;
});

// Response interceptor for token refresh
api.interceptors.response.use(
  response => {
    // Simply return successful responses
    return response;
  },
  async error => {
    // Handle network errors and server errors (5xx)
    const originalRequest = error.config;

    // Don't attempt refresh if it's already a refresh request or during initialization
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
      originalRequest._retry = true;

      try {
        // Use TokenManager to get refresh token atomically
        const { refreshToken, source } = tokenManager.getTokens();

        if (refreshToken) {
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            {
              refresh_token: refreshToken,
            },
            {
              timeout: 5000, // Shorter timeout for refresh
            }
          );

          const { access_token } = response.data.tokens || { access_token: response.data.accessToken };

          // Use TokenManager to store tokens atomically (preserve remember me preference)
          const rememberMe = source === 'local';
          await tokenManager.setTokens(access_token, refreshToken, rememberMe);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens atomically using TokenManager
        tokenManager.clearTokens();

        // Only redirect if not during app initialization (profile check)
        if (!originalRequest.url?.includes('/auth/profile')) {
          // Use React Router navigation instead of window.location
          // This will be handled by the App component
        }

        return Promise.reject(refreshError);
      }
    }

    // For network errors, provide a better error message
    if (!error.response) {
      error.message = 'Network error. Please check your connection and try again.';
    }

    return Promise.reject(error);
  }
);

export interface LoginRequest {
  email: string;
  password: string;
  role?: 'parent' | 'student' | 'teacher' | 'admin';
}

export interface RegisterRequest {
  email: string;
  username: string;
  password: string;
  parentName: string;
  parentEmail?: string;
  childBirthdate?: string;
  parentConsent: boolean;
  gdprConsent?: boolean;
  termsAccepted: boolean;
  marketingConsent?: boolean;
  // Alternative field names for API compatibility
  primary_parent_email?: string;
  family_name?: string;
  communication_preferences?: {
    email_notifications?: boolean;
    preferred_language?: string;
  };
}

export interface AuthResponse {
  message: string;
  data: User;
  tokens: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };
}

class AuthService {
  async login(credentials: LoginRequest): Promise<User> {
    const startTime = Date.now();

    try {
      // CRITICAL FIX: Use native fetch instead of axios to bypass axios configuration issues
      // The backend API works perfectly, but axios isn't making the request
      const fetchResponse = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(credentials),
      });

      if (!fetchResponse.ok) {
        const errorData = await fetchResponse.json().catch(() => ({ message: 'Login failed' }));
        throw new Error(errorData.message || `HTTP ${fetchResponse.status}: ${fetchResponse.statusText}`);
      }

      const responseData = await fetchResponse.json();

      const { data, user, tokens, accessToken, refreshToken } = responseData;

      // Handle both response formats (some endpoints use different field names)
      // Backend might return just { token: "...", user: {...} } for simple cases
      const userData = data || user || responseData;
      const access_token = tokens?.access_token || accessToken || responseData.access_token || responseData.token;
      const refresh_token = tokens?.refresh_token || refreshToken || responseData.refresh_token;

      // CRITICAL FIX: Use TokenManager ONLY for token storage to avoid race conditions
      // TokenManager handles locks, atomicity, and storage in the correct location
      // Previous duplicate storage was causing race conditions where tokens were cleared
      if (access_token) {
        // Store tokens using TokenManager (handles locks, atomicity, and backward compatibility keys)
        await tokenManager.setTokens(access_token, refresh_token, true);

        // Set auth cookie for additional persistence across subdomains
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 7); // 7 days
        document.cookie = `auth_token=${encodeURIComponent(access_token)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
      }

      // Map user data to expected User interface
      const mappedUser: User = {
        user_id: userData.user_id || userData.id,
        family_id: userData.family_id,
        email: userData.email,
        role: userData.role || 'parent', // Use database role or default to parent
        permissions: userData.permissions || [],
        account_type: userData.account_type, // CRITICAL: Account type for routing (individual, family, school, corporate)
        // Optional fields
        family_name: userData.family_name,
        student_ids: userData.student_ids,
        grade_level: userData.grade_level,
        age_group: userData.age_group,
        subscription_status: userData.subscription_status,
      };

      return mappedUser;
    } catch (error: any) {
      const elapsed = Date.now() - startTime;
      console.error('Login failed after:', elapsed, 'ms', error);

      // Handle timeout specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error(
          'Authentication is taking too long. Please try again. If the problem persists, contact support.'
        );
      }

      // Handle network errors
      if (!error.response) {
        console.error('Network error details:', {
          code: error.code,
          message: error.message,
          name: error.name,
          stack: error.stack,
        });

        // Specific error messages for different network issues
        if (error.code === 'ERR_NETWORK') {
          throw new Error(
            'Network error: Unable to reach authentication service. Please check your internet connection.'
          );
        } else if (error.code === 'ERR_NAME_NOT_RESOLVED') {
          throw new Error('DNS error: Unable to resolve authentication service address. Please try again later.');
        } else {
          throw new Error(
            `Connection failed: ${error.message || 'Unable to connect to authentication service. Please check your connection and try again.'}`
          );
        }
      }

      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      throw new Error(errorMessage);
    }
  }

  async register(registrationData: RegisterRequest): Promise<User> {
    const startTime = Date.now();
    // Debug logging removed for production - browser capabilities checked at runtime

    // Transform registration data to match API expectations
    const apiData = {
      primary_parent_email: registrationData.primary_parent_email || registrationData.email,
      family_name: registrationData.family_name || registrationData.parentName,
      password: registrationData.password,
      email: registrationData.email,
      username: registrationData.username,
      parentName: registrationData.parentName,
      parentEmail: registrationData.parentEmail || registrationData.email,
      childBirthdate: registrationData.childBirthdate,
      parentConsent: registrationData.parentConsent,
      gdprConsent: registrationData.gdprConsent,
      termsAccepted: registrationData.termsAccepted,
      marketingConsent: registrationData.marketingConsent,
      communication_preferences: registrationData.communication_preferences || {
        email_notifications: registrationData.marketingConsent || true,
        preferred_language: 'en',
      },
    };

    try {
      // Simple approach without AbortController for maximum compatibility
      const response = await api.post<AuthResponse>('/auth/register', apiData);

      const { data, user, tokens, accessToken, refreshToken } = response.data;

      // Handle both response formats
      const userData = data || user;
      const access_token = tokens?.access_token || accessToken;
      const refresh_token = tokens?.refresh_token || refreshToken;

      // Store tokens using TokenManager (default to remember me for registration)
      await tokenManager.setTokens(access_token, refresh_token, true);

      // Map user data to expected User interface
      const mappedUser: User = {
        user_id: userData.user_id || userData.id,
        family_id: userData.family_id,
        email: userData.email,
        role: userData.role || 'parent', // Default to parent for registration
        permissions: userData.permissions || [],
        // Optional fields
        family_name: userData.family_name,
        student_ids: userData.student_ids,
        grade_level: userData.grade_level,
        age_group: userData.age_group,
        subscription_status: userData.subscription_status,
      };

      return mappedUser;
    } catch (error: any) {
      const elapsed = Date.now() - startTime;
      console.error('Registration failed after:', elapsed, 'ms');
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        response: error.response,
        stack: error.stack,
      });

      // Handle timeout specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('Registration is taking too long. Please try again. If the problem persists, contact support.');
      }

      // Handle network errors
      if (!error.response) {
        console.error('Network error - no response from server', error);
        throw new Error('Unable to connect to registration service. Please check your connection and try again.');
      }

      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      throw new Error(errorMessage);
    }
  }

  async getProfile(): Promise<User> {
    try {
      const response = await api.get<{ data: User }>('/auth/profile');

      // Handle the response data structure
      const userData = response.data?.data || response.data;

      // Ensure we have required fields with defaults
      const user: User = {
        user_id: userData.user_id || userData.id,
        family_id: userData.family_id,
        email: userData.email,
        role: userData.role || 'parent', // Default to parent role if missing
        account_type: userData.account_type, // Include account_type from backend
        permissions: userData.permissions || [],
        // Optional fields
        family_name: userData.family_name,
        student_ids: userData.student_ids,
        grade_level: userData.grade_level,
        age_group: userData.age_group,
        subscription_status: userData.subscription_status,
      };

      return user;
    } catch (error: any) {
      // Log the specific error for debugging
      console.error('Profile fetch failed:', error.response?.status, error.message);
      const errorMessage = error.response?.data?.message || 'Failed to get user profile.';
      throw new Error(errorMessage);
    }
  }

  async refreshToken(): Promise<string> {
    try {
      // Use TokenManager to get refresh token atomically
      const { refreshToken, source } = tokenManager.getTokens();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post<{ tokens: { access_token: string } }>('/auth/refresh', {
        refresh_token: refreshToken,
      });

      const { access_token } = response.data.tokens || { access_token: response.data.accessToken };

      // Use TokenManager to store tokens atomically (preserve remember me preference)
      const rememberMe = source === 'local';
      await tokenManager.setTokens(access_token, refreshToken, rememberMe);

      return access_token;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Token refresh failed.';
      throw new Error(errorMessage);
    }
  }

  async logout(): Promise<void> {
    try {
      // Call backend logout endpoint to invalidate server-side session
      await api.post('/auth/logout');
    } catch (error) {
      // Logout endpoint failure is not critical - we still need to clear client state
      console.warn('Logout API call failed, continuing with client-side cleanup:', error);
    } finally {
      // CRITICAL: Clear ALL token storage locations
      tokenManager.clearTokens();

      // Also clear any sigma08_jwt_token that might have been set during login
      try {
        localStorage.removeItem('sigma08_jwt_token');
        sessionStorage.removeItem('sigma08_jwt_token');
      } catch (e) {
        console.warn('Could not clear jwt tokens:', e);
      }

      // Clear the Zustand persisted auth store
      try {
        localStorage.removeItem('sigma08-auth');
      } catch (e) {
        console.warn('Could not clear auth store:', e);
      }
    }
  }

  isAuthenticated(): boolean {
    // Use TokenManager to check authentication atomically
    const { accessToken } = tokenManager.getTokens();
    return !!accessToken;
  }

  getToken(): string | null {
    // Use TokenManager to get token atomically
    const { accessToken } = tokenManager.getTokens();
    return accessToken;
  }

  async requestPasswordReset(email: string): Promise<void> {
    try {
      const response = await api.post('/auth/forgot-password', { email });

      // API should return success message
      if (response.status !== 200) {
        throw new Error('Failed to send password reset email');
      }
    } catch (error: any) {
      // Handle specific error cases
      if (!error.response) {
        throw new Error('Network error. Please check your connection and try again.');
      }

      const errorMessage = error.response?.data?.message || 'Failed to send password reset email. Please try again.';
      throw new Error(errorMessage);
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        password: newPassword,
      });

      if (response.status !== 200) {
        throw new Error('Failed to reset password');
      }
    } catch (error: any) {
      if (!error.response) {
        throw new Error('Network error. Please check your connection and try again.');
      }

      const errorMessage = error.response?.data?.message || 'Failed to reset password. Please try again.';
      throw new Error(errorMessage);
    }
  }
}

export const authService = new AuthService();
export { api };
